import { useEffect } from 'react'

import { getFirebase, execute } from '~/firebase'
import useCurrentUser from '~/hooks/useCurrentUser'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { EventData } from '~/gamification/types/gamification.types'

import {
  updateEventsActionCreator,
  selectEvents,
  setEventsActionCreator,
} from '~/gamification/slices/gamification.slice'

import { isAuthenticated, selectIsFirebaseAuthenticated } from '~/auth/slices/auth.slice'

export function useGamification(): void {
  const hasFetched = Boolean(useAppSelector(selectEvents))
  const user = useCurrentUser()
  const authenticated = useAppSelector(isAuthenticated)
  const dispatch = useAppDispatch()
  const isReady = useAppSelector(selectIsFirebaseAuthenticated)

  useEffect(() => {
    if (!authenticated || !isReady || !user) return
    const fetchEvents = async (): Promise<void> => {
      const initialEvents: EventData[] = []
      const { query, collection, fireStore, getDocs } = await getFirebase()
      const q = query(collection(fireStore, 'users', String(user.id), 'events'))
      const eventsCollection = await getDocs(q)
      eventsCollection.docs.forEach((_doc) => {
        const event = _doc.data() as EventData
        // eslint-disable-next-line fp/no-mutating-methods
        initialEvents.push(event)
      })
      dispatch(setEventsActionCreator(initialEvents))
    }
    fetchEvents()
  }, [isReady, dispatch, user, authenticated])

  useEffect(() => {
    if (!hasFetched || !user) return

    const run = async (): Promise<() => void> => {
      const { fireStore, onSnapshot, query, collection } = await getFirebase()

      const eventsRef = query(collection(fireStore, 'users', String(user.id), 'events'))
      const unsubscribeEvents = onSnapshot(eventsRef, (querySnapshot) => {
        querySnapshot.docChanges().forEach((change) => {
          if (change.type === 'added' || change.type === 'modified') {
            const eventData = change.doc.data() as EventData
            dispatch(
              updateEventsActionCreator({
                events: [eventData],
                reduxOnly: true,
              }),
            )
          }
        })
      })

      return () => {
        unsubscribeEvents()
      }
    }

    const cleanup = run()
    return () => {
      execute(cleanup)
    }
  }, [hasFetched, dispatch, user])
}
